import { BrowserModule, Title } from '@angular/platform-browser';
import { NgbCarouselModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { APP_INITIALIZER, Injectable, NgModule } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, HttpClientModule, HttpHeaders, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DefaultUrlSerializer, Router, UrlSerializer, UrlTree } from '@angular/router';
import { MainComponent } from './components/main/main.component';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { AppRoutingModule } from './app-routing.module';
import { ShopModule } from './components/shop/shop.module';
import { SharedModule } from './components/shared/shared.module';
import { ColorOptionsComponent } from './components/color-options/color-options.component';
import { NotTranslatedService } from './components/shared/services/not-ranslated.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MyLoaderComponent } from './components/my-loader/my-loader.component';
import { LoaderService } from './components/shared/services/loader.service';
import { LoaderInterceptor } from './components/shared/services/interceptors/loader-interceptor.service';
import { ConfigService } from './components/shared/services/config.service';
import { environment } from 'src/environments/environment';
import { CommonSetting } from './modals/commonsetting.modal';
import { PagesModule } from './components/pages/pages.module';
import { FindEnrollerComponent } from './components/shared/model/findenroller/findenroller.component';
import { PersistentService } from './components/shared/services/persistent.service';
import { ApiInterceptor } from './components/shared/services/interceptors/http-interceptor.service';
import { CookieService } from 'ngx-cookie-service';
import { getSubdomain } from 'tldts';
import { AuthGuard } from './components/shared/services/auth.guard.service';
import { LoggedInAuthGuard } from './components/shared/services/loggedin.guard.service';
import { APP_BASE_HREF } from '@angular/common';
import { getBaseLocation } from './baseUrl';
import { UserServiceModal } from './modals/userservice.modal';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { WelcomeComponent } from './components/welcome/welcome.component';

const initializeConfig = (
  configService: ConfigService,
  http: HttpClient,
  dialog: MatDialog,
  translate
) => {
  const userData = JSON.parse(sessionStorage.getItem("userService") || '{}');
  const pathSegments = window.location.pathname.split('/');
  const webAliasFromRoute = pathSegments[1];
  const webAliasFromUser = webAliasFromRoute || userData?.customerData?.WebAlias || configService.DefaultWebalias;

  const fetchWebsiteInfo = (webAlias: string): Observable<any> => {
    return http.get<any>(`${environment.apiUrl}api/Customers/GetWebsiteInformation_V1?webAlias=${webAlias}`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' })
    });
  };

  const localConfig$ = http.get<any>('assets/data/localconfig.json', {
    headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' })
  });

  const clientSetting$ = http.get<CommonSetting>('assets/data/clientsetting.json', {
    headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' }),
    withCredentials: true
  }).pipe(
    tap((settings) => configService.init(settings))
  );

  const websiteInfo$ = fetchWebsiteInfo(webAliasFromUser).pipe(
    switchMap((result: any) => {
      if (!result.Data) {
        return fetchWebsiteInfo(configService.DefaultWebalias).pipe(
          tap((defaultResult: any) => {
            configService.setWebsiteInfo(defaultResult.Data);
          }),
          switchMap(() => localConfig$),
          tap((localSettings) => configService.localSettings = localSettings),
          tap(() => {
            const pathSegments = window.location.pathname.split('/');
            pathSegments[1] = configService.DefaultWebalias;
            const newUrl = `${location.origin}/${pathSegments.join('/')}`;
            window.location.href = newUrl;
          }),
          map((defaultResult: any) => defaultResult.Data)
        );
      } else {
        configService.setWebsiteInfo(result.Data);
        return localConfig$.pipe(
          tap((localSettings) => configService.localSettings = localSettings),
          map(() => result.Data)
        );
      }
    }),
    catchError((err) => {
      console.error('Website info load failed', err);
      return of(null);
    })
  );

  return () => {
    return forkJoin([websiteInfo$, clientSetting$])
      .toPromise()
      .then((res) => res);
  };
};

@Injectable()
export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams): any {
    if (params.interpolateParams) {
      return params.interpolateParams['Default'] || params.key;
    }
    return params.key;
  }

  constructor(private nts: NotTranslatedService) { }
}

@Injectable()
// export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
//   parse(url: string): UrlTree {
//     return super.parse(url.toLowerCase());
//   }
// }
@NgModule({
  declarations: [
    AppComponent,
    MainComponent,
    ColorOptionsComponent,
    MyLoaderComponent,
    WelcomeComponent,
  ],
  imports: [
    NgxSpinnerModule,
    BrowserModule,
    NgbModule,
    SharedModule,
    ShopModule,
    PagesModule,
    NgbModule,
    NgbCarouselModule,
    NgxSkeletonLoaderModule,
    HttpClientModule,
    BrowserAnimationsModule,
    NoopAnimationsModule,
    FormsModule,
    MatDialogModule,
    ReactiveFormsModule,
    AppRoutingModule,
    NgxImageZoomModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
        deps: [NotTranslatedService]
      }
    })
  ],
  providers: [

    UserServiceModal,
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseLocation
    },
    ConfigService,
    { provide: APP_INITIALIZER, useFactory: initializeConfig, deps: [ConfigService, HttpClient, MatDialog], multi: true },
    LoaderService,
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    Title,
    PersistentService,
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: function (router: Router, cookieService: CookieService) {
        return new ApiInterceptor(router, cookieService);
      },
      multi: true,
      deps: [Router, CookieService]
    },
    AuthGuard,
    LoggedInAuthGuard
  ],
  exports: [
    MatDialogModule
  ],
  bootstrap: [AppComponent],

})
export class AppModule { }

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
